/**
 * Analytics Tracking Utilities
 *
 * Centralized analytics tracking for Firebase/Google Analytics
 * Provides consistent event tracking across the application
 */

import { logEvent } from 'firebase/analytics';
import { getAnalyticsInstance } from './firebase';
import { isProduction, logDebug, logError } from './logger';
import { isBot } from './botDetection';

/**
 * Analytics event types
 */
export const ANALYTICS_EVENTS = {
  // Ticket-related events
  TICKET_CLICK: 'ticket_click',
  TICKET_PROVIDER_VIEW: 'ticket_provider_view',

  // Game info events
  GAME_INFO_OPEN: 'game_info_open',
  GAME_INFO_CLOSE: 'game_info_close',
  MAP_VIEW: 'map_view',
  WEATHER_VIEW: 'weather_view',

  // Search events
  WEB_SEARCH_OPEN: 'web_search_open',
  WEB_SEARCH_QUERY: 'web_search_query',

  // Navigation events
  EXTERNAL_LINK_CLICK: 'external_link_click',
};

/**
 * Track ticket provider click
 * @param {string} provider - Provider name (stubhub, viagogo, etc.)
 * @param {Object} gameInfo - Game information
 */
export const trackTicketClick = (provider, gameInfo = {}) => {
  // Skip analytics for bots/crawlers
  if (isBot()) {
    return;
  }

  const analytics = getAnalyticsInstance();
  if (!analytics || !analytics.app || !isProduction()) {
    logDebug(`[Analytics] Ticket click: ${provider}`, gameInfo);
    return;
  }

  // Build pubref for tracking
  const pubref =
    gameInfo.team && gameInfo.opponent
      ? `${gameInfo.league || 'game'}_${gameInfo.team}_vs_${gameInfo.opponent}`
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
      : 'unknown';

  // Determine if deep linking is being used
  const hasDeepLink = !!(gameInfo.team && gameInfo.opponent);

  logEvent(analytics, ANALYTICS_EVENTS.TICKET_CLICK, {
    provider,
    team: gameInfo.team || 'unknown',
    opponent: gameInfo.opponent || 'unknown',
    league: gameInfo.league || 'unknown',
    pubref,
    adref: gameInfo.placement || 'game_info_ticket_button',
    ab_test_mode: process.env.REACT_APP_TICKET_PROVIDER || 'random',
    deep_link_enabled: hasDeepLink,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track game info modal open
 * @param {Object} gameInfo - Game information
 */
export const trackGameInfoOpen = (gameInfo = {}) => {
  // Skip analytics for bots/crawlers
  if (isBot()) {
    return;
  }

  const analytics = getAnalyticsInstance();
  if (!analytics || !analytics.app || !isProduction()) {
    logDebug('[Analytics] Game info open', gameInfo);
    return;
  }

  logEvent(analytics, ANALYTICS_EVENTS.GAME_INFO_OPEN, {
    content_type: 'game_info',
    content_id: `${gameInfo.team || 'unknown'} - ${gameInfo.opponent || 'unknown'}`,
    team: gameInfo.team,
    opponent: gameInfo.opponent,
  });
};

/**
 * Track map view
 * @param {Object} locationInfo - Location information
 */
export const trackMapView = (locationInfo = {}) => {
  // Skip analytics for bots/crawlers
  if (isBot()) {
    return;
  }

  const analytics = getAnalyticsInstance();
  if (!analytics || !analytics.app || !isProduction()) {
    logDebug('[Analytics] Map view', locationInfo);
    return;
  }

  logEvent(analytics, ANALYTICS_EVENTS.MAP_VIEW, {
    stadium: locationInfo.stadium || 'unknown',
    city: locationInfo.city || 'unknown',
    region: locationInfo.region || 'unknown',
  });
};

/**
 * Track weather view
 * @param {Object} weatherInfo - Weather information
 */
export const trackWeatherView = (weatherInfo = {}) => {
  // Skip analytics for bots/crawlers
  if (isBot()) {
    return;
  }

  const analytics = getAnalyticsInstance();
  if (!analytics || !analytics.app || !isProduction()) {
    logDebug('[Analytics] Weather view', weatherInfo);
    return;
  }

  logEvent(analytics, ANALYTICS_EVENTS.WEATHER_VIEW, {
    date: weatherInfo.date || 'unknown',
    zipCode: weatherInfo.zipCode || 'unknown',
  });
};

/**
 * Track web search query
 * @param {string} query - Search query
 * @param {Object} context - Search context
 */
export const trackWebSearch = (query, context = {}) => {
  // Skip analytics for bots/crawlers
  if (isBot()) {
    return;
  }

  const analytics = getAnalyticsInstance();
  if (!analytics || !analytics.app || !isProduction()) {
    logDebug('[Analytics] Web search', { query, ...context });
    return;
  }

  logEvent(analytics, ANALYTICS_EVENTS.WEB_SEARCH_QUERY, {
    search_term: query,
    team: context.team || 'unknown',
    opponent: context.opponent || 'unknown',
  });
};

/**
 * Track external link click
 * @param {string} url - External URL
 * @param {string} linkType - Type of link (ticket, flight, etc.)
 */
export const trackExternalLink = (url, linkType = 'unknown') => {
  // Skip analytics for bots/crawlers
  if (isBot()) {
    return;
  }

  const analytics = getAnalyticsInstance();
  if (!analytics || !analytics.app || !isProduction()) {
    logDebug('[Analytics] External link', { url, linkType });
    return;
  }

  logEvent(analytics, ANALYTICS_EVENTS.EXTERNAL_LINK_CLICK, {
    link_url: url,
    link_type: linkType,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Generic event tracker
 * @param {string} eventName - Event name
 * @param {Object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  // Skip analytics for bots/crawlers
  if (isBot()) {
    return;
  }

  const analytics = getAnalyticsInstance();
  if (!analytics || !analytics.app || !isProduction()) {
    logDebug(`[Analytics] Event: ${eventName}`, eventParams);
    return;
  }

  try {
    logEvent(analytics, eventName, eventParams);
  } catch (error) {
    logError('Failed to log analytics event', error);
  }
};

export default {
  ANALYTICS_EVENTS,
  trackTicketClick,
  trackGameInfoOpen,
  trackMapView,
  trackWeatherView,
  trackWebSearch,
  trackExternalLink,
  trackEvent,
};
